import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, galleryHeaders, mediaUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import PhotoGrid, { SelectionBar } from "@/components/PhotoGrid";
import Lightbox from "@/components/Lightbox";
import { GoldButton } from "@/components/Brand";
import { useFavorites, downloadZip } from "@/lib/gallery";

export default function AlbumPage() {
  const { slug, albumSlug } = useParams();
  const { t, path, localized } = useI18n();
  const { favorites, toggleFavorite, addManyFavorites } = useFavorites();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/events/${slug}/albums/${albumSlug}`, {
        params: { page: 1, page_size: 120 },
        headers: galleryHeaders(slug),
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 403 ? "403" : "404");
    }
  }, [slug, albumSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (error)
    return (
      <div className="p-12">
        <p className="text-sm text-[#8F3329]">
          {error === "403" ? t("privacy.noticePassword") : "404"}
        </p>
        <Link to={path("events", slug)} className="mt-4 inline-block text-sm underline">
          {t("btn.back")}
        </Link>
      </div>
    );
  if (!data) return <p className="p-12 text-sm text-[#5A5752]">{t("common.loading")}</p>;

  const { event, album, media, download_mode: mode } = data;
  const canDownloadMulti = ["multiple", "album", "event"].includes(mode);
  const canDownloadAlbum = ["album", "event"].includes(mode);
  const toggleSelect = (m) =>
    setSelected((prev) => (prev.includes(m.id) ? prev.filter((id) => id !== m.id) : [...prev, m.id]));

  const downloadOne = async (m) => {
    if (!m.can_download) return toast.error(t("gallery.noDownloads"));
    window.open(mediaUrl(m, "orig", slug), "_blank");
  };

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12 sm:px-8 lg:px-12 lg:py-16">
      <nav aria-label="Breadcrumb" className="mb-8 text-xs text-[#5A5752]" data-testid="album-breadcrumb">
        <Link to={path("events")} className="hover:text-[#8F3329]">
          {t("events.title")}
        </Link>
        <span className="mx-2 text-[#C69A3B]">/</span>
        <Link to={path("events", slug)} className="hover:text-[#8F3329]">
          {localized(event, "name")}
        </Link>
        <span className="mx-2 text-[#C69A3B]">/</span>
        <span className="text-[#252525]">{localized(album, "name")}</span>
      </nav>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="font-serif-display text-3xl tracking-tight text-[#8F3329] sm:text-4xl">
            {localized(album, "name")}
          </h1>
          {localized(album, "description") && (
            <p className="mt-3 max-w-xl text-sm text-[#5A5752]">{localized(album, "description")}</p>
          )}
          <p className="label-mono mt-4">
            {data.total} {t("events.items")}
          </p>
        </div>
        {canDownloadAlbum && (
          <GoldButton
            variant="outline"
            data-testid="download-entire-album-btn"
            onClick={async () => {
              try {
                await downloadZip({ album_id: album.id }, slug);
                toast.success(t("gallery.zipReady"));
              } catch {
                toast.error(t("gallery.noDownloads"));
              }
            }}
          >
            {t("btn.downloadAll")}
          </GoldButton>
        )}
      </div>

      <div className="gold-rule my-9" />

      <PhotoGrid
        media={media}
        slug={slug}
        onOpen={(i) => {
          const item = media[i];
          if (item?.kind === "video" && item.source_url) window.open(item.source_url, "_blank", "noopener");
          else setLightbox(i);
        }}
        selected={selected}
        onToggleSelect={canDownloadMulti ? toggleSelect : toggleSelect}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
      />

      <SelectionBar
        count={selected.length}
        canDownload={canDownloadMulti}
        onClear={() => setSelected([])}
        onFavorite={() => addManyFavorites(selected)}
        onDownload={async () => {
          try {
            await downloadZip({ media_ids: selected }, slug);
            toast.success(t("gallery.zipReady"));
          } catch {
            toast.error(t("gallery.noDownloads"));
          }
        }}
      />

      {lightbox !== null && (
        <Lightbox
          items={media}
          index={lightbox}
          slug={slug}
          onClose={() => setLightbox(null)}
          onIndexChange={setLightbox}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          selected={selected}
          onToggleSelect={toggleSelect}
          canDownload={Boolean(media[lightbox]?.can_download)}
          onDownload={downloadOne}
          onShare={
            event.sharing_enabled && event.privacy === "public"
              ? () => {
                  navigator.clipboard?.writeText(window.location.href);
                  toast.success(t("gallery.linkCopied"));
                }
              : null
          }
        />
      )}
    </div>
  );
}
